import { ProductLaneConfig } from '@habity/types';

/**
 * Hook for initializing and using the ProductLane widget
 *
 * Based on the official ProductLane documentation:
 * https://productlane.com/docs/get-started/widget
 */
export const useProductLane = () => {
  const init = (config: ProductLaneConfig) => {
    // Only run on the client side
    if (typeof window === 'undefined') return;

    const { widgetKey } = config || {};
    if (!widgetKey) return;

    // Setup the ProductLane queue object
    // This is directly from the ProductLane documentation
    if (typeof window.Productlane === 'undefined') {
      // Create the queue object first
      window.Productlane = { queue: {} };

      // Setup the methods that will queue calls until the script loads
      const methods = ['set', 'open', 'close', 'toggle', 'on', 'off', 'init'];
      methods.forEach((method) => {
        // Create a function that will queue calls with their arguments
        // We need to use any here to match the exact ProductLane initialization pattern
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window.Productlane as any)[method] = function (...args: unknown[]) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const queue = (window.Productlane as any).queue as Record<
            string,
            { args: unknown[] }
          >;
          queue[method] = { args };
        };
      });
    }

    // Initialize ProductLane with settings
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const productlane = window.Productlane as any;
    if (productlane && typeof productlane.init === 'function') {
      productlane.init(config);
    }
  };

  return { init };
};

// Add the type definition for the ProductLane global object
declare global {
  interface Window {
    Productlane?: unknown;
  }
}
